import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { createCustomer, updateCustomer } from '../../services/customerService';
import ContactManager from './ContactManager';
import './CustomerForm.css';

/**
 * CustomerForm Component
 * 
 * Form for creating or editing customer information with validation
 * and error handling. It is self-contained and manages its own state.
 * 
 * @param {Object} customer - Optional: Existing customer data (for edit mode)
 * @param {Function} onSave - Callback function called with the saved customer object after a successful save.
 * @param {Function} onCancel - Callback function for the cancel button.
 */
const CustomerForm = ({ customer, onSave, onCancel }) => {
  const isEditMode = !!customer;
  
  // Form data state
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    contacts: []
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validated, setValidated] = useState(false);
  
  // Load customer data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      setFormData({
        company_name: customer.company_name || customer.name || '',
        address: customer.address || '',
        contacts: customer.contacts || []
      });
    }
  }, [customer, isEditMode]);
  
  /**
   * Handle form field changes
   * @param {Event} e - Change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  /**
   * Handle contacts change from ContactManager
   * @param {Array} contacts - Updated contacts array
   */
  const handleContactsChange = (contacts) => {
    setFormData(prev => ({ ...prev, contacts }));
    if (error) setError(null);
    if (success) setSuccess(false);
  };
  
  /**
   * Validate form data
   * @returns {Object} - Validation result with isValid boolean and error message
   */
  const validateForm = () => {
    // Company name is required
    if (!formData.company_name.trim()) {
      return { isValid: false, error: 'Company name is required' };
    }

    // Validate contacts (if any are provided, they should have names)
    for (const contact of formData.contacts) {
      if (!contact.name.trim()) {
        return { isValid: false, error: 'All contacts must have a name' };
      }
      
      // Validate email format if provided
      if (contact.email && contact.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contact.email.trim())) {
          return { isValid: false, error: 'Please enter valid email addresses for all contacts' };
        }
      }
    }

    return { isValid: true };
  };
  
  /**
   * Handle form submission
   * @param {Event} e - Submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setError(null);
    setSuccess(false);
    
    // Custom validation
    const validation = validateForm();
    if (!validation.isValid) {
      setError(validation.error);
      setValidated(true);
      return;
    }
    
    // Prepare data for submission
    const submitData = {
      company_name: formData.company_name.trim(),
      address: formData.address.trim() || null,
      contacts: formData.contacts.map(contact => ({
        name: contact.name.trim(),
        email: contact.email.trim() || null,
        phone_numbers: contact.phone_numbers?.filter(phone => phone.number.trim()).map(phone => ({
          number: phone.number.trim(),
          type: phone.type
        })) || []
      }))
    };
    
    setLoading(true);
    
    try {
      let result;
      
      if (isEditMode) {
        result = await updateCustomer(customer.id, submitData);
      } else {
        result = await createCustomer(submitData);
      }
      
      setSuccess(true);
      
      // Call the onSave callback with the saved customer object
      if (onSave) {
        // Use a short timeout to allow the success message to be visible before the modal closes
        setTimeout(() => {
          onSave(result);
        }, 800);
      }
      
    } catch (err) {
      console.error('Failed to save customer:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      // Don't set loading to false immediately if successful, to show the success state on the button
      if (!success) {
        setLoading(false);
      }
    }
  };
  
  return (
    <Card className="shadow-sm customer-form customer-form-card border-0">
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success">
            <strong>Success!</strong> Customer has been {isEditMode ? 'updated' : 'saved'}.
          </Alert>
        )}

        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3" controlId="companyName">
                <Form.Label>Company Name*</Form.Label>
                <Form.Control
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Enter company or business name"
                  required
                  autoFocus
                  disabled={loading || success}
                  isInvalid={validated && !formData.company_name.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  Company name is required.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-4" controlId="address">
                <Form.Label>Billing Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Full billing address"
                  disabled={loading || success}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <ContactManager
                contacts={formData.contacts}
                onChange={handleContactsChange}
                disabled={loading || success}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={onCancel}
              disabled={loading || success}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || success}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : success ? (
                isEditMode ? 'Updated!' : 'Saved!'
              ) : (
                isEditMode ? 'Update Customer' : 'Save Customer'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CustomerForm;